import { Grid, Divider, Button } from 'semantic-ui-react'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  GridRowColumn,
  Modal,
  Text,
  Button as HeardButton,
} from '@/componentsV1/BaseComponents'
import { VirtualStatementData } from '@/actions/admin/accountReconciliationActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { VirtualStatementTransactionTable } from './VirtualStatementTransactionTable'
import { reportsCoveredByReconciliation } from '../../user-transactions-panels/helpers'
import {
  blockedRecoveryMessage,
  goToBlockedOnBooksLabel,
  reconciliationFailureHeading,
} from '../blockedRecovery'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { getUserFinancialAccountById } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import {
  hasLimitedBankAccess,
  hasPlaidStatements,
} from '@/features/user/settings/helpers'
import { selectBookkeepingReportsByUserId } from '@/selectors/bookkeepingReportsSelectors'
import { Colors } from '@/src/styles/theme'

export const VirtualStatement = ({
  toggleOpen,
  data,
  onReconcileClick,
  reconcileLocked,
}: {
  toggleOpen: () => void
  data: VirtualStatementData | null
  reconcileLocked: boolean
  onReconcileClick: (
    formType: string,
    account: FinancialAccountWithAdminInfo,
    reconType: 'auto' | 'manual' | 'recover'
  ) => void
}) => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const bookkeepingReports = useReselector(
    selectBookkeepingReportsByUserId,
    userId
  )

  const userFinancialAccount = useReselector(
    getUserFinancialAccountById,
    userId,
    data?.reconciliation?.financialAccount.id
  )

  const reportMonths = useMemo(() => {
    if (!data?.reconciliation) return []
    const filteredReports = reportsCoveredByReconciliation(
      bookkeepingReports,
      data.reconciliation
    )

    return filteredReports.map((report) => ({
      reportId: report.id,
      dateString: DateTime.fromISO(report.startDate, { zone: 'utc' }).toFormat(
        DATE_FORMATS_LUXON.MONTH_YEAR_LONG
      ),
    }))
  }, [bookkeepingReports, data?.reconciliation])

  const currentMonth = useMemo(
    () =>
      data?.currentReport?.date
        ? DateTime.fromISO(data?.currentReport?.date, {
            zone: 'utc',
          }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
        : null,
    [data?.currentReport?.date]
  )
  const earliestOpenMonth = useMemo(
    () =>
      data?.earliestBlockingReport?.date
        ? DateTime.fromISO(data?.earliestBlockingReport?.date, {
            zone: 'utc',
          }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
        : null,
    [data?.earliestBlockingReport?.date]
  )
  const hasPlaidStatement = useMemo(
    () => userFinancialAccount && hasPlaidStatements(userFinancialAccount),
    [userFinancialAccount]
  )
  const hasLBA = useMemo(
    () =>
      userFinancialAccount &&
      !hasPlaidStatement &&
      hasLimitedBankAccess(userFinancialAccount),
    [userFinancialAccount, hasPlaidStatement]
  )

  if (!data) return null

  const { reconciliation, statement, statementUrl, blocked } = data
  const failureHeading = reconciliationFailureHeading(blocked)
  const {
    endingBalanceDate,
    endingBalanceInCents,
    startingBalanceDate,
    startingBalanceInCents,
    status,
    financialAccount,
  } = reconciliation

  const formattedStartDate = DateTime.fromISO(startingBalanceDate, {
    zone: 'utc',
  }).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)

  const formattedEndDate = DateTime.fromISO(endingBalanceDate, {
    zone: 'utc',
  }).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)

  const openStatement = () => {
    if (statementUrl) {
      window.open(statementUrl, '_blank')
    }
  }

  const isComplete = status === 'complete'

  const handleManualReconcile = () => {
    if (userFinancialAccount) {
      onReconcileClick(reconciliation.status, userFinancialAccount, 'manual')
      toggleOpen()
    }
  }

  const handleGoToReport = () => {
    if (reportMonths?.[0]?.reportId) {
      navigate(
        `/admin/finances/records/${userId}/monthly-books/${reportMonths[0].reportId}`
      )
      toggleOpen()
    }
  }

  return (
    <Modal
      open={Boolean(data)}
      onClose={toggleOpen}
      closeIcon
      size="fullscreen"
    >
      <Modal.Content>
        <Grid>
          <GridRowColumn>
            <Text as="h2">
              {isComplete ? 'Auto-reconciliation complete' : failureHeading}
            </Text>
          </GridRowColumn>
          <Grid.Row>
            <Grid.Column width={5}>
              <Grid.Column>
                <Text> {financialAccount.plaidInstitutionName} </Text>
                <Text>
                  <b>{financialAccount.name}</b>: {financialAccount.mask}
                </Text>
                <Text>
                  {financialAccount.type} -- {financialAccount.subtype}
                </Text>
              </Grid.Column>
            </Grid.Column>
            <Grid.Column width={11}>
              {isComplete ? (
                <Alert type="success">
                  <Text color="green">Successfully auto-reconciled</Text>
                </Alert>
              ) : (
                <Alert type="error">
                  <Grid stackable>
                    {!blocked &&
                      reconciliation.bookkeepingProcess !== 'catchUp' && (
                        <Grid.Row>
                          <Grid.Column width={8}>
                            <Text color="red">Auto-reconciliation failed</Text>
                            <Text color="red" as="bodySm">
                              {statementUrl
                                ? 'Please manually reconcile against the bank statement.'
                                : 'Please manually reconcile against the bank statement once it becomes available.'}
                            </Text>
                            {statementUrl && hasPlaidStatement && (
                              <Text as="bodyXs">
                                Statement pulled via Plaid Statements
                              </Text>
                            )}
                            {hasLBA && (
                              <Text as="bodyXs">
                                Statement accessed via LBA
                              </Text>
                            )}
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <HeardButton
                              variant="secondary"
                              onClick={openStatement}
                              disabled={!statementUrl}
                            >
                              View Statement
                            </HeardButton>
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <Button
                              color="blue"
                              onClick={handleManualReconcile}
                              disabled={reconcileLocked}
                            >
                              Manually Reconcile
                            </Button>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                    {blocked && (
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <Text color="red">{failureHeading}</Text>
                          <Text color="red" as="bodySm">
                            {blockedRecoveryMessage(
                              earliestOpenMonth,
                              currentMonth
                            )}
                          </Text>
                        </Grid.Column>
                        {data?.earliestBlockingReport?.id && (
                          <Grid.Column width={4}>
                            <Button
                              color="blue"
                              onClick={() => {
                                toggleOpen()
                                navigate(
                                  `/admin/finances/records/${userId}/monthly-books/${data.earliestBlockingReport?.id}`
                                )
                              }}
                            >
                              {goToBlockedOnBooksLabel(earliestOpenMonth)}
                            </Button>
                          </Grid.Column>
                        )}
                      </Grid.Row>
                    )}
                    {!blocked &&
                      reconciliation.bookkeepingProcess === 'catchUp' && (
                        <Grid.Row verticalAlign="middle">
                          <Grid.Column width={10}>
                            <Text color="red" as="h3">
                              Onboarding auto-reconciliation failed
                            </Text>
                            <Text color="red">
                              Please manually reconcile these months:
                            </Text>
                            <ul>
                              {reportMonths.map((reportMonth) => (
                                <li
                                  key={reportMonth.reportId}
                                  style={{ color: Colors.red }}
                                >
                                  <Text color="red">
                                    {reportMonth.dateString}
                                  </Text>
                                </li>
                              ))}
                            </ul>
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <HeardButton
                              variant="secondary"
                              onClick={handleGoToReport}
                              disabled={!reportMonths?.[0]?.reportId}
                            >
                              Go to {reportMonths[0].dateString}
                            </HeardButton>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                  </Grid>
                </Alert>
              )}
            </Grid.Column>
          </Grid.Row>
          <GridRowColumn>
            <Divider style={{ margin: 0 }} />
          </GridRowColumn>
          <GridRowColumn>
            <Text as="h2"> Virtual bank statement </Text>
          </GridRowColumn>
          <Grid.Row>
            <Grid.Column width={6}>
              <Text> Starting Heard Balance ({formattedStartDate}) </Text>
              <Text style={{ marginTop: 4 }}>
                <b>
                  <CurrencyFormatLabel
                    value={centsToDollars(startingBalanceInCents)}
                  />
                </b>
              </Text>
            </Grid.Column>
            <Grid.Column width={6}>
              <Text> Ending Plaid Balance ({formattedEndDate}) </Text>
              <Text
                style={{ marginTop: 4 }}
                color={!isComplete ? 'red' : undefined}
              >
                <b>
                  <CurrencyFormatLabel
                    value={centsToDollars(endingBalanceInCents)}
                  />
                </b>
              </Text>
            </Grid.Column>
          </Grid.Row>
          <GridRowColumn>
            {Boolean(statement?.length) && (
              <VirtualStatementTransactionTable statement={statement} />
            )}
          </GridRowColumn>
        </Grid>
      </Modal.Content>
    </Modal>
  )
}
